/**
 * Version history list for a document.
 *
 * Renders the immutable version timeline (newest first) with each version's
 * number, author, status, optional note, and a traceability link when the
 * version was committed from a source (`source_ref`). Selecting two versions
 * drives the {@link DiffView} (old → subsequent → current). Metadata only —
 * no clinical content is shown or logged.
 */
import React from "react";
import { sourceDisplayLabel } from "./proposalLabels";
import { humanizeStatus } from "../../utils/statusLabels";
import type { VersionSummary } from "../../types";

/** Props for {@link VersionList}. */
interface VersionListProps {
  /** Version metadata (any order; rendered newest first). */
  versions: VersionSummary[];
  /** The currently selected "from" version number, if any. */
  fromNo: number | null;
  /** The currently selected "to" version number, if any. */
  toNo: number | null;
  /** Select a version (toggles the from/to pair). */
  onSelect: (versionNo: number) => void;
  /** Load a version's content into the editor for editing + commit. */
  onLoad: (versionNo: number) => void;
  /**
   * Restore a committed version as the new current version (one click). Offered
   * only for past committed versions (the currently active version is hidden) — a
   * proposed AI version must be adjudicated through the confirmation box, never
   * restored (ADR-010).
   */
  onRestore: (versionNo: number) => void;
  /** The document's current committed version number, if any. */
  currentVersionNo?: number | null;
}

/**
 * Render the version history with selectable rows for diffing.
 *
 * @param props - The versions and the selected diff pair + select handler.
 * @returns The version-list element.
 */
export const VersionList: React.FC<VersionListProps> = ({
  versions,
  fromNo,
  toNo,
  onSelect,
  onLoad,
  onRestore,
  currentVersionNo,
}) => {
  if (versions.length === 0) {
    return (
      <p className="text-sm text-slate-400 italic">
        No versions committed yet.
      </p>
    );
  }
  const ordered = [...versions].sort((a, b) => b.version_no - a.version_no);
  return (
    <ul className="space-y-2" data-testid="version-list">
      {ordered.map((v) => {
        const selected = v.version_no === fromNo || v.version_no === toNo;
        // No resolved title is available for a version's source_ref here — a
        // backend-scheme ref (e.g. `chat:<thread>`, `derivative:<id>`) is
        // suppressed rather than shown raw; a plain reference id (e.g.
        // `SES-001`) is acceptable display text (sourceDisplayLabel's
        // established contract).
        const versionSourceLabel = v.source_ref
          ? sourceDisplayLabel({ source_ref: v.source_ref })
          : null;
        return (
          <li key={v.id}>
            <button
              type="button"
              onClick={() => onSelect(v.version_no)}
              aria-pressed={selected}
              className={`w-full text-left rounded-lg border px-3 py-2 ${
                selected
                  ? "border-blue-400 bg-blue-50"
                  : "border-slate-200 hover:bg-slate-50"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-800 text-sm">
                  v{v.version_no}
                </span>
                <span className="text-[11px] uppercase tracking-wide text-slate-400">
                  {humanizeStatus(v.status)}
                  {v.attested ? " · attested" : ""}
                </span>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{v.author}</div>
              {v.note && (
                <div className="text-xs text-slate-600 mt-1">{v.note}</div>
              )}
              {versionSourceLabel !== null && (
                <div className="text-[11px] text-slate-400 mt-1 font-mono">
                  source: {versionSourceLabel}
                </div>
              )}
            </button>
            <div className="mt-1 flex items-center gap-3">
              <button
                type="button"
                data-testid={`load-version-${v.version_no}`}
                onClick={() => onLoad(v.version_no)}
                className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
              >
                {v.status === "proposed"
                  ? "Load proposal into editor"
                  : "Load into editor"}
              </button>
              {v.status !== "proposed" && v.version_no !== currentVersionNo && (
                <button
                  type="button"
                  data-testid={`restore-version-${v.version_no}`}
                  onClick={() => onRestore(v.version_no)}
                  className="text-xs font-medium text-blue-700 hover:text-blue-900 hover:underline"
                >
                  Restore this version
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default VersionList;
